import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import FileSaver from "file-saver";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import CloseIcon from "@mui/icons-material/Close";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { grey, blue, amber } from "@mui/material/colors";

import RenderInputField from "components/Reports/ReusableComponents/RenderInputField/RenderInputField";
import StoreMultiSelect from "./StoreMultiSelect";
import {
	buildReportFilterColumns,
	MULTI_FILTER_KEYS,
	MRP_GROUP_COLUMN,
	DEFAULT_REPORT_FILTERS,
} from "./reportFilters";
import { API, REPORT_MODES } from "../data";
import { STATUS_ORDER } from "./reportUi";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import parseErrMsgFromError from "utils/errorFunctions/parseErrorMessageFromError";

const SCOPE_KEYS = ["mode", "store_id", "status", "grade", "city_tier"];
const MRP_ONLY_KEYS = ["division", "category"];

const EMPTY_OPTIONS = { stores: [], statuses: STATUS_ORDER, grades: [], city_tiers: [], divisions: [], categories: [] };

const FieldLabel = ({ children }) => (
	<Typography fontSize={11.5} fontWeight={700} color="text.secondary" mb={0.5}>
		{children}
	</Typography>
);

const SectionHeading = ({ icon, title, subtitle }) => (
	<Stack direction="row" spacing={1} alignItems="flex-start" mb={1.5}>
		<Box sx={{ color: blue[600], display: "flex", mt: 0.25 }}>{icon}</Box>
		<Box>
			<Typography fontSize={13} fontWeight={700} color="text.primary">
				{title}
			</Typography>
			{subtitle ? (
				<Typography fontSize={11.5} color="text.secondary">
					{subtitle}
				</Typography>
			) : null}
		</Box>
	</Stack>
);

/** Export modal — lets the user re-scope the store-level filters before
 * generating the three-sheet (Manual + Monthly + MRP projection) Excel via
 * the Python VM. Fully self-contained: it fetches its own filter option set
 * (stores/grades/tiers/divisions/categories) scoped to its own selections,
 * so it can be opened from anywhere for a given plan — it doesn't depend on
 * the Plan Report tab having been visited or having any particular filters
 * applied. */
const ExportReportModal = ({ open, onClose, plan, showError, showSuccess }) => {
	const [exportFilters, setExportFilters] = useState(DEFAULT_REPORT_FILTERS);
	const [mrpGrain, setMrpGrain] = useState("raw");
	const [groupBy, setGroupBy] = useState(DEFAULT_REPORT_FILTERS.group_by);
	const [downloading, setDownloading] = useState(false);
	const [modalOptions, setModalOptions] = useState(EMPTY_OPTIONS);
	const [optionsLoading, setOptionsLoading] = useState(false);

	const scopeKey = SCOPE_KEYS.map((key) => {
		const value = exportFilters[key];
		return Array.isArray(value) ? value.join(",") : value || "";
	}).join("|");

	useEffect(() => {
		if (!plan?.id) return;
		let cancelled = false;

		const load = async () => {
			setOptionsLoading(true);
			try {
				const params = { section: "filter_options", mode: exportFilters.mode || DEFAULT_REPORT_FILTERS.mode };
				MULTI_FILTER_KEYS.forEach((key) => {
					if (key === "division" || key === "category") return;
					const value = exportFilters[key];
					if (Array.isArray(value) && value.length) params[key] = value.join(",");
				});
				const { data } = await axios.get(API.GET_PLAN_REPORT(plan.id), { params });
				if (cancelled) return;
				if (data.status === "success") {
					setModalOptions({
						stores: data.result.stores || [],
						statuses: data.result.statuses?.length ? data.result.statuses : STATUS_ORDER,
						grades: data.result.grades || [],
						city_tiers: data.result.city_tiers || [],
						divisions: data.result.divisions || [],
						categories: data.result.categories || [],
					});
				}
			} catch (err) {
				console.error("Failed to load export filter options", err);
			} finally {
				if (!cancelled) setOptionsLoading(false);
			}
		};

		load();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [plan?.id, scopeKey]);

	const filterColumns = useMemo(() => buildReportFilterColumns(modalOptions), [modalOptions]);
	const scopeColumns = filterColumns.filter((c) => SCOPE_KEYS.includes(c.key));
	const mrpColumns = filterColumns.filter((c) => MRP_ONLY_KEYS.includes(c.key));

	const activeScopeCount = SCOPE_KEYS.filter((key) => {
		if (key === "mode") return false;
		const value = exportFilters[key];
		return Array.isArray(value) && value.length;
	}).length;
	const activeMrpCount = MRP_ONLY_KEYS.filter((key) => (exportFilters[key] || []).length).length;

	const currentMode = exportFilters.mode || DEFAULT_REPORT_FILTERS.mode;
	// Division/Category (and, by extension, the export itself) come up empty
	// whenever no store in scope has reached the selected Submission Type yet —
	// e.g. everything is still DRAFT but "Latest submitted"/"Final approved" is
	// selected. Surface that plainly instead of leaving a silent empty dropdown.
	const showNoDataHint =
		!optionsLoading && currentMode !== "LATEST_ANY" && !modalOptions.divisions.length && !modalOptions.categories.length;

	const handleFilterChange = (next) => setExportFilters((prev) => ({ ...prev, ...next }));

	const handleExport = async () => {
		setDownloading(true);
		try {
			const filter = { plan_id: plan?.id, mrp_grain: mrpGrain, group_by: groupBy };
			MULTI_FILTER_KEYS.forEach((key) => {
				const value = exportFilters[key];
				if (Array.isArray(value) && value.length) filter[key] = value;
			});
			filter.mode = exportFilters.mode || DEFAULT_REPORT_FILTERS.mode;

			const res = await pythonVmInstance({
				url: "/business_plan_v2/report/export",
				method: "POST",
				data: { filter },
				responseType: "blob",
			});

			const blobUrl = URL.createObjectURL(res.data);
			const fileName = `BP_${plan?.name || "Report"}_${new Date().toISOString().split("T")[0]}.xlsx`;
			FileSaver.saveAs(blobUrl, fileName);
			showSuccess?.("Report exported successfully");
			onClose();
		} catch (err) {
			showError?.(err ? parseErrMsgFromError(err) : "Failed to export report");
		} finally {
			setDownloading(false);
		}
	};

	const renderField = (column) => (
		<Box key={column.key}>
			<FieldLabel>{column.name}</FieldLabel>
			{column.key === "store_id" ? (
				<StoreMultiSelect
					options={column.option_data.list}
					value={exportFilters.store_id}
					onChange={(keys) => handleFilterChange({ store_id: keys })}
					width="100%"
					placeholder="All stores"
				/>
			) : (
				<RenderInputField
					column={column}
					data={exportFilters}
					setData={handleFilterChange}
					width="100%"
					selectOptObjName="option_data"
					disableLabel
				/>
			)}
		</Box>
	);

	return (
		<Dialog open={open} onClose={downloading ? undefined : onClose} maxWidth="md" fullWidth>
			<DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1.5 }}>
				<Box>
					<Typography fontSize={16} fontWeight={700}>
						Export Plan Report
					</Typography>
					<Typography fontSize={12} color="text.secondary" fontWeight={400}>
						Generates an Excel workbook with a Manual Projection sheet and an MRP Projection sheet
					</Typography>
				</Box>
				<IconButton size="small" onClick={onClose} disabled={downloading}>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>
			<DialogContent dividers sx={{ bgcolor: grey[50] }}>
				<Box sx={{ bgcolor: "#fff", border: `1px solid ${grey[200]}`, borderRadius: 2.5, p: 2, mb: 2 }}>
					<Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
						<SectionHeading
							icon={<TableChartOutlinedIcon fontSize="small" />}
							title="Store scope"
							subtitle="Applies to both the Manual and MRP sheets"
						/>
						{activeScopeCount ? (
							<Chip
								size="small"
								label={`${activeScopeCount} filter${activeScopeCount > 1 ? "s" : ""} applied`}
								sx={{ bgcolor: blue[50], color: blue[700], fontWeight: 700, fontSize: 11 }}
							/>
						) : null}
					</Stack>
					<Grid container spacing={2}>
						{scopeColumns.map((column) => (
							<Grid item xs={12} sm={6} md={4} key={column.key}>
								{renderField(column)}
							</Grid>
						))}
					</Grid>
				</Box>

				<Box sx={{ bgcolor: "#fff", border: `1px solid ${grey[200]}`, borderRadius: 2.5, p: 2 }}>
					<Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
						<SectionHeading
							icon={<Inventory2OutlinedIcon fontSize="small" />}
							title="MRP sheet filters"
							subtitle="Only narrows the MRP Projection sheet — Manual Projection is unaffected"
						/>
						<Stack direction="row" spacing={1} alignItems="center">
							{optionsLoading ? <CircularProgress size={13} /> : null}
							{activeMrpCount ? (
								<Chip
									size="small"
									label={`${activeMrpCount} filter${activeMrpCount > 1 ? "s" : ""} applied`}
									sx={{ bgcolor: blue[50], color: blue[700], fontWeight: 700, fontSize: 11 }}
								/>
							) : null}
						</Stack>
					</Stack>

					{showNoDataHint ? (
						<Stack
							direction="row"
							spacing={1.25}
							alignItems="center"
							sx={{ bgcolor: amber[50], border: `1px solid ${amber[200]}`, borderRadius: 1.5, px: 1.5, py: 1, mb: 2 }}
						>
							<WarningAmberRoundedIcon fontSize="small" sx={{ color: amber[800] }} />
							<Typography fontSize={12} color={amber[900]} sx={{ flex: 1 }}>
								No stores in the current scope have reached "{REPORT_MODES.find((m) => m.key === currentMode)?.label}" yet, so there's nothing to filter on (or export).
							</Typography>
							<Button
								size="small"
								onClick={() => handleFilterChange({ mode: "LATEST_ANY" })}
								sx={{ textTransform: "none", fontWeight: 700, whiteSpace: "nowrap" }}
							>
								Use "Latest (any status)"
							</Button>
						</Stack>
					) : null}

					<Grid container spacing={2} mb={2}>
						{mrpColumns.map((column) => (
							<Grid item xs={12} sm={6} key={column.key}>
								{renderField(column)}
							</Grid>
						))}
					</Grid>

					<Divider sx={{ my: 2 }} />

					<Grid container spacing={2} alignItems="flex-start">
						<Grid item xs={12} sm={groupBy && mrpGrain === "grouped" ? 6 : 12}>
							<FieldLabel>Sheet detail</FieldLabel>
							<ToggleButtonGroup
								size="small"
								exclusive
								fullWidth
								value={mrpGrain}
								onChange={(e, value) => value && setMrpGrain(value)}
								sx={{ bgcolor: grey[100], borderRadius: 1.5, p: 0.25 }}
							>
								<ToggleButton
									value="raw"
									sx={{ fontSize: 12, textTransform: "none", border: "none", borderRadius: 1.25 }}
								>
									Raw rows (full detail)
								</ToggleButton>
								<ToggleButton
									value="grouped"
									sx={{ fontSize: 12, textTransform: "none", border: "none", borderRadius: 1.25 }}
								>
									Grouped / pivoted
								</ToggleButton>
							</ToggleButtonGroup>
						</Grid>

						{mrpGrain === "grouped" ? (
							<Grid item xs={12} sm={6}>
								<FieldLabel>Group by</FieldLabel>
								<RenderInputField
									column={MRP_GROUP_COLUMN}
									data={{ group_by: groupBy }}
									setData={(next) => setGroupBy(next.group_by)}
									width="100%"
									selectOptObjName="option_data"
									disableLabel
								/>
							</Grid>
						) : null}
					</Grid>
				</Box>
			</DialogContent>
			<DialogActions sx={{ px: 3, py: 2 }}>
				<Button onClick={onClose} disabled={downloading} sx={{ textTransform: "none" }}>
					Cancel
				</Button>
				<Button
					variant="contained"
					onClick={handleExport}
					disabled={downloading || !plan?.id}
					startIcon={downloading ? <CircularProgress size={14} color="inherit" /> : null}
					sx={{ textTransform: "none" }}
				>
					{downloading ? "Generating…" : "Export"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ExportReportModal;
